import React from 'react';

interface PythagoreanSquareCell {
  position: number;
  name: string;
  values: number[];
  count: number;
  interpretation: string;
}

interface PythagoreanSquareData {
  cells: PythagoreanSquareCell[];
  grid: number[][];
  energyBalance: {
    strong: string[];
    weak: string[];
    balanced: string[];
  };
  aiInterpretation?: string;
}

interface Props {
  data: PythagoreanSquareData;
}

const CELL_COLORS: Record<number, string> = {
  1: '#ff6b6b', 2: '#ffa94d', 3: '#ffd43b',
  4: '#69db7c', 5: '#4dabf7', 6: '#9775fa',
  7: '#f783ac', 8: '#38d9a9', 9: '#74c0fc',
};

const PythagoreanSquare: React.FC<Props> = ({ data }) => {
  const { cells, energyBalance } = data;

  return (
    <div style={{
      background: 'rgba(10, 20, 40, 0.85)',
      border: '2px solid rgba(147, 130, 220, 0.5)',
      borderRadius: '20px',
      padding: '30px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(147, 130, 220, 0.2)',
    }}>
      <h2 style={{
        color: '#c4b5fd',
        fontSize: '24px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '24px',
        fontFamily: 'serif',
      }}>
        Пифагорейский Квадрат
      </h2>

      {/* 3x3 Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        maxWidth: '420px',
        margin: '0 auto 24px',
      }}>
        {cells.map((cell) => (
          <div key={cell.position} style={{
            background: 'rgba(20, 10, 50, 0.8)',
            border: `2px solid ${CELL_COLORS[cell.position]}40`,
            borderRadius: '12px',
            padding: '12px',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            cursor: 'default',
            minHeight: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <div style={{
              fontSize: '10px',
              color: 'rgba(200, 180, 255, 0.6)',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              {cell.name}
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: CELL_COLORS[cell.position],
              marginBottom: '6px',
            }}>
              {cell.position}
            </div>
            <div style={{
              display: 'flex',
              gap: '3px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              {cell.count === 0 ? (
                <span style={{ fontSize: '12px', color: 'rgba(200,180,255,0.3)' }}>пусто</span>
              ) : (
                cell.values.map((v, i) => (
                  <span key={i} style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: `${CELL_COLORS[cell.position]}30`,
                    border: `1px solid ${CELL_COLORS[cell.position]}80`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: CELL_COLORS[cell.position],
                    fontWeight: 'bold',
                  }}>
                    {v}
                  </span>
                ))
              )}
            </div>
            <div style={{
              fontSize: '9px',
              color: 'rgba(200,180,255,0.5)',
              marginTop: '6px',
              lineHeight: '1.3',
            }}>
              {cell.count > 0 ? `${cell.count} шт.` : 'нет энергии'}
            </div>
          </div>
        ))}
      </div>

      {/* Energy Balance */}
      <div style={{
        background: 'rgba(20, 10, 50, 0.6)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px',
      }}>
        <h3 style={{
          color: '#c4b5fd',
          fontSize: '16px',
          marginBottom: '12px',
          textAlign: 'center',
        }}>
          Баланс энергий
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {energyBalance.strong.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#69db7c',
                flexShrink: 0,
              }} />
              <span style={{ color: '#69db7c', fontSize: '13px' }}>
                Сильные: {energyBalance.strong.join(', ')}
              </span>
            </div>
          )}
          {energyBalance.balanced.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#4dabf7',
                flexShrink: 0,
              }} />
              <span style={{ color: '#4dabf7', fontSize: '13px' }}>
                Баланс: {energyBalance.balanced.join(', ')}
              </span>
            </div>
          )}
          {energyBalance.weak.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#f783ac',
                flexShrink: 0,
              }} />
              <span style={{ color: '#f783ac', fontSize: '13px' }}>
                Слабые: {energyBalance.weak.join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* AI Interpretation */}
      {data.aiInterpretation && (
        <div style={{
          background: 'rgba(30, 20, 60, 0.6)',
          border: '1px solid rgba(147, 130, 220, 0.3)',
          borderRadius: '12px',
          padding: '16px',
        }}>
          <h3 style={{
            color: '#c4b5fd',
            fontSize: '14px',
            marginBottom: '10px',
          }}>
            Интерпретация
          </h3>
          <div style={{
            color: 'rgba(220, 210, 255, 0.9)',
            fontSize: '13px',
            lineHeight: '1.7',
            whiteSpace: 'pre-wrap',
          }}>
            {data.aiInterpretation}
          </div>
        </div>
      )}
    </div>
  );
};

export default PythagoreanSquare;
